import { LaptopOutlined, LogoutOutlined } from "@ant-design/icons";
import { Menu } from "antd";
import Layout, { Content } from "antd/es/layout/layout";
import { CONTENT_PADDING_BOTTOM } from "layout/MainLayout";
import Sider from "antd/es/layout/Sider";
import { showConfirmModal } from "components/modals/confirmModal";
import SpottLogo from "components/logos/SpottLogo.component";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { FULL_VIEWPORT_HEIGHT } from "shared/constants/zoom";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { hardNavigateIfUpdated } from "shared/utils/appVersion";

const SystemAdminLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = [
    {
      key: PATHS.BUSINESS,
      label: translate("business"),
      icon: <LaptopOutlined />,
    },
    {
      key: PATHS.LOGOUT,
      label: translate("button.logout"),
      icon: <LogoutOutlined />,
    },
  ];

  return (
    <Layout style={{ minHeight: FULL_VIEWPORT_HEIGHT }}>
      <Sider
        theme="light"
        breakpoint="lg"
        collapsedWidth="70"
        onCollapse={(collapsed, type) => {
          setCollapsed(collapsed);
        }}
      >
        <SpottLogo
          textSize={5}
          style={{ padding: 20 }}
          collapsed={collapsed}
        />
        <Menu
          theme="light"
          mode="inline"
          defaultSelectedKeys={["1"]}
          items={menuItems}
          onClick={(e) => {
            // Logout is the only single-click-to-consequence item in this
            // sidebar — Business just navigates, but this ends the session.
            // A stray click (this menu item sits right next to Business,
            // and there's nowhere else to click in this small sidebar)
            // shouldn't sign the admin out with no way back.
            if (e.key === PATHS.LOGOUT) {
              showConfirmModal({
                title: translate("messages.warning.logout.title"),
                content: translate("messages.warning.logout.content"),
                confirmLabel: translate("button.logout"),
                onConfirm: () => navigate(PATHS.LOGOUT),
              });
              return;
            }
            // A sidebar click is the safe moment to pick up a new deploy: it
            // already abandons the current page, so a full page load here can
            // never lose anything. AntD fires onClick for leaf items only, so
            // e.key is always a real route path.
            if (hardNavigateIfUpdated(e.key)) return;
            navigate(e.key);
          }}
        />
      </Sider>
      <Layout
        style={{
          backgroundColor: colors.page,
          paddingLeft: 30,
          paddingRight: 30,
          paddingBottom: CONTENT_PADDING_BOTTOM,
        }}
      >
        <Content>{children}</Content>
      </Layout>
    </Layout>
  );
};

export default SystemAdminLayout;
